import type { MouseEventHandler } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import humanizeDaysjs from '@/helpers/humanizeDaysjs';
import { getColor } from '@/helpers/utils';
import parse from 'html-react-parser';
import type { DOMNode } from 'html-react-parser';
import { Element } from 'html-react-parser';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import type {
  NotificationChannel,
  NotificationRow
} from '@/app/common/api/notifications/types/inappApiTypes';

function isTagElement(node: DOMNode): node is Element {
  return node.type === 'tag';
}

const renderMessage = (msg: string, expired: boolean | undefined) => {
  return parse(msg, {
    replace: (domNode: DOMNode) => {
      if (isTagElement(domNode) && domNode.name === 'a' && expired) {
        return (
          <ChildrensTooltip
            placement="top"
            delayToRemove={0}
            html={'بعد از ۷ روز لینک منقضی شد.'}
          >
            <span className="text-600">منقضی شد</span>
          </ChildrensTooltip>
        );
      }
    }
  });
};

export type NotificationProps = NotificationRow & {
  expired?: boolean;
  className?: string;
  flush?: boolean;
  deleteHandler?: MouseEventHandler<SVGSVGElement>;
  readHandler?: MouseEventHandler<HTMLDivElement>;
  channel?: NotificationChannel;
};

const Notification = ({
  message_id,
  message,
  is_read,
  expired,
  created_at,
  className,
  flush = false,
  deleteHandler,
  readHandler,
  channel
}: NotificationProps) => {
  const relativeTime = humanizeDaysjs(created_at);
  const decodedMessage = decodeURIComponent(message);

  return (
    <div
      key={message_id}
      className={classNames(
        'notification cursor-pointer hover-actions-trigger position-relative',
        {
          'notification-unread': !is_read,
          'notification-flush': flush,
          'cursor-pointer': channel === 'private'
        },
        className
      )}
      style={{
        borderRight: !is_read
          ? `3px solid ${getColor('primary')}`
          : '3px solid transparent'
      }}
      onClick={readHandler}
    >
      <div className="notification-body">
        <div className="d-flex align-items-start gap-2 mb-1">
          <p className="mb-0 flex-grow-1">
            {renderMessage(decodedMessage, expired)}
          </p>

          {channel === 'private' && (
            <div
              className="text-400 fs-1 flex-shrink-0"
              onClick={e => e.stopPropagation()}
            >
              <FontAwesomeIcon
                onClick={deleteHandler}
                className="text-danger fs-0"
                icon="trash-alt"
                transform="shrink-3 down-4"
              />
            </div>
          )}
        </div>

        <span className="text-500 fs--2">{relativeTime}</span>
      </div>
    </div>
  );
};

export default Notification;
